import { getApiUrl } from "src/utils";
import {
  TrustPaymentsCountryCurrencyMap,
  TrustPaymentsLocalCurrencyResponse,
  TrustPaymentsCreateSessionPayload,
  TrustPaymentsCreateSessionResponse,
  TrustPaymentsCreateWithdrawalPayload,
  TrustPaymentsCreateWithdrawalResponse,
  TrustPaymentsFxQuoteRequest,
  TrustPaymentsFxQuoteResponse,
  TrustPaymentsSessionData,
  TrustPaymentsUpdateSessionPayload,
  TrustPaymentsUpdateSessionResponse,
} from "./types";

export enum TrustPaymentsEndpoints {
  SESSION = "/psp/trustpayments/session",
  SESSION_UPDATE = "/psp/trustpayments/session/update",
  WITHDRAWAL = "/psp/trustpayments/withdrawal",
  COUNTRY_CURRENCY = "/psp/trustpayments/country-currency",
  LOCAL_CURRENCY = "/psp/trustpayments/local-currency",
  QUOTE_DEPOSIT = "/psp/trustpayments/quote-deposit",
  QUOTE_WITHDRAWAL = "/psp/trustpayments/quote-withdrawal",
}

// Thin REST client. vakotrade owns JWT-signing, session cache, and
// the Trust webservices integration — widget only needs these calls.
class TrustPaymentsClient {
  private apiUrl: string;
  private authorization = "";

  constructor(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  public setToken(token?: string): void {
    if (token) this.authorization = token;
  }

  private async request<T>(
    method: "GET" | "POST",
    endpoint: string,
    options?: {
      body?: Record<string, unknown>;
      params?: Record<string, string>;
    }
  ): Promise<T> {
    const queryString = options?.params ? "?" + new URLSearchParams(options.params).toString() : "";

    // Host passes its GraphQL endpoint as `apiUrl`; strip `/graphql`
    // so REST calls land on the NestJS HTTP root.
    const baseUrl = this.apiUrl.replace("/graphql", "");

    const traceId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : "";

    const response = await fetch(`${baseUrl}${endpoint}${queryString}`, {
      method,
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        ...(traceId ? { "X-Trace-Id": traceId } : {}),
        ...(this.authorization ? { Authorization: this.authorization } : {}),
      },
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorBody}`);
    }

    return (await response.json()) as T;
  }

  public async createSession(payload: TrustPaymentsCreateSessionPayload): Promise<TrustPaymentsCreateSessionResponse> {
    return this.request<TrustPaymentsCreateSessionResponse>("POST", TrustPaymentsEndpoints.SESSION, {
      body: payload as unknown as Record<string, unknown>,
    });
  }

  public async getSession(sessionId: string): Promise<TrustPaymentsSessionData> {
    return this.request<TrustPaymentsSessionData>("GET", TrustPaymentsEndpoints.SESSION, { params: { session_id: sessionId } });
  }

  public async updateSessionKyc(payload: TrustPaymentsUpdateSessionPayload): Promise<TrustPaymentsUpdateSessionResponse> {
    return this.request<TrustPaymentsUpdateSessionResponse>("POST", TrustPaymentsEndpoints.SESSION_UPDATE, {
      body: payload as unknown as Record<string, unknown>,
    });
  }

  public async createWithdrawal(
    payload: TrustPaymentsCreateWithdrawalPayload,
  ): Promise<TrustPaymentsCreateWithdrawalResponse> {
    return this.request<TrustPaymentsCreateWithdrawalResponse>("POST", TrustPaymentsEndpoints.WITHDRAWAL, {
      body: payload as unknown as Record<string, unknown>,
    });
  }

  // Country -> ISO 4217 currency map. Widget bootstraps once, falls
  // through to existing USD/EUR UI if the user's country isn't mapped.
  public async getCountryCurrencyMap(): Promise<TrustPaymentsCountryCurrencyMap> {
    return this.request<TrustPaymentsCountryCurrencyMap>("GET", TrustPaymentsEndpoints.COUNTRY_CURRENCY);
  }

  // Local fiat from the user's KYC country — shown before the user types.
  public async getLocalCurrency(): Promise<TrustPaymentsLocalCurrencyResponse> {
    return this.request<TrustPaymentsLocalCurrencyResponse>("GET", TrustPaymentsEndpoints.LOCAL_CURRENCY);
  }

  // BE applies route markup + platform/provider fees, locks FX rate
  // server-side. `fx: true` means the route is FX-enabled and the
  // widget should switch to local-currency display + pass `quote_id`
  // back at session creation. `fx: false` means existing UI is correct
  // and `quote_id` must NOT be passed.
  public async quoteDeposit(payload: TrustPaymentsFxQuoteRequest): Promise<TrustPaymentsFxQuoteResponse> {
    return this.request<TrustPaymentsFxQuoteResponse>("POST", TrustPaymentsEndpoints.QUOTE_DEPOSIT, {
      body: payload as unknown as Record<string, unknown>,
    });
  }

  public async quoteWithdrawal(payload: TrustPaymentsFxQuoteRequest): Promise<TrustPaymentsFxQuoteResponse> {
    return this.request<TrustPaymentsFxQuoteResponse>("POST", TrustPaymentsEndpoints.QUOTE_WITHDRAWAL, {
      body: payload as unknown as Record<string, unknown>,
    });
  }
}

export const trustPaymentsService = new TrustPaymentsClient(getApiUrl() ?? "");
